import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../../components/ui';
import { DynamicColumn, DynamicListTable } from '../../../components/DynamicListTable';
import { NumerosGrid, TipoNumerosGrid } from '../../../components/NumerosGrid';
import { fetchAPI } from '../../../services/apiClient';
import { fechaLocalYMD } from '../../../utils/date';
import { Printer } from 'lucide-react';
import { isNativePrintSelected, printListByDestination } from '../../../utils/printer';

interface SorteoItem {
  id: string;
  tipo_id: string;
  nombre: string;
  fecha: string;
  estado: string;
  jugadas_hoy?: number;
}

interface ResumenLocalRow {
  local_id: string;
  local_nombre: string;
  total_jugadas: number;
  total_vendedores: number;
  total_monto: number;
}

const monto = (value: unknown): string => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '0';
  return Math.trunc(n).toLocaleString('es-CR');
};

const mapMontos = (rows: any[], keys: string[]): any[] =>
  rows.map((row, index) => {
    const out = { ...row, id: row.id ?? index + 1 };
    for (const key of keys) {
      if (key in out) out[key] = monto(out[key]);
    }
    return out;
  });




const columnsByTipo = (tipo: string): DynamicColumn[] => {
  switch (tipo) {
    case 'NORMAL':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '120px' },
        { key: 'vendido', label: 'MONTO', type: 'readonly', width: '180px' },
      ];
    case 'REVENTADO':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '120px' },
        { key: 'vendido_rev1', label: 'REV1', type: 'readonly', width: '160px' },
        { key: 'vendido_rev2', label: 'REV2', type: 'readonly', width: '160px' },
      ];
    case 'MEGA':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '100px' },
        { key: 'rev1', label: 'REV1', type: 'readonly', width: '120px' },
        { key: 'rev2', label: 'REV2', type: 'readonly', width: '120px' },
        { key: 'num2', label: 'NÚM2', type: 'readonly', width: '80px' },
        { key: 'monto_mega', label: 'MONTO', type: 'readonly', width: '130px' },
      ];
    case 'MONAZOS':
      return [
        { key: 'numero', label: 'NUM', type: 'readonly', width: '120px' },
        { key: 'tipo', label: 'TIPO', type: 'readonly', width: '120px' },
        { key: 'vendido', label: 'MONTO', type: 'readonly', width: '180px' },
      ];
    case 'FECHAS':
      return [
        { key: 'numero', label: 'FECHA', type: 'readonly', width: '120px' },
        { key: 'vendido', label: 'MONTO', type: 'readonly', width: '180px' },
      ];
    case 'PARLEY':
      return [
        { key: 'num1', label: 'NUM1', type: 'readonly', width: '120px' },
        { key: 'num2', label: 'NUM2', type: 'readonly', width: '120px' },
        { key: 'vendido', label: 'MONTO', type: 'readonly', width: '180px' },
      ];
    default:
      return [];
  }
};

const montoKeysByTipo: Record<string, string[]> = {
  NORMAL: ['vendido'],
  REVENTADO: ['vendido_rev1', 'vendido_rev2'],
  MEGA: ['rev1', 'rev2', 'monto_mega'],
  MONAZOS: ['vendido'],
  FECHAS: ['vendido'],
  PARLEY: ['vendido'],
};

export const LocalListasPage: React.FC = () => {
  const [sorteos, setSorteos] = useState<SorteoItem[]>([]);
  const [selectedSorteoId, setSelectedSorteoId] = useState('');
  const [fechaFiltro, setFechaFiltro] = useState(fechaLocalYMD);
  const [rows, setRows] = useState<any[]>([]);
  const [resumen, setResumen] = useState<ResumenLocalRow | null>(null);
  const [loadingRows, setLoadingRows] = useState(false);

  // Workflow interfaces
  interface ListaVendedorRecibida {
    id: string;
    vendedor_id: string;
    vendedor_nombre: string;
    estado: string;
    enviada_at: string | null;
    jugadas_count: number;
  }
  interface MiListaLocal {
    id: string;
    estado: string;
    listas_vendedor_count: number;
    jugadas_count: number;
    enviada_banca_at: string | null;
  }

  const [recibidas, setRecibidas] = useState<ListaVendedorRecibida[]>([]);
  const [miLista, setMiLista] = useState<MiListaLocal | null>(null);
  const [seleccionadas, setSeleccionadas] = useState<string[]>([]);
  const [loadingWorkflow, setLoadingWorkflow] = useState(false);
  const [msgWorkflow, setMsgWorkflow] = useState('');
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);
  const [printSuccess, setPrintSuccess] = useState(false);

  const selectedSorteo = useMemo(
    () => sorteos.find((s) => s.id === selectedSorteoId) || null,
    [sorteos, selectedSorteoId],
  );

  useEffect(() => {
    fetchAPI<SorteoItem[]>(`/sorteos?fecha=${fechaFiltro}`)
      .then((data) => {
        setSorteos(data);
        setSelectedSorteoId((prev) => {
          if (!data.length) return '';
          const match = data.find((s) => s.id === prev);
          if (match) return prev;
          // Preferir sorteo con más jugadas hoy
          const conData = [...data].sort((a, b) => (b.jugadas_hoy ?? 0) - (a.jugadas_hoy ?? 0));
          return (conData[0]?.jugadas_hoy ?? 0) > 0 ? conData[0].id : data[0].id;
        });
      })
      .catch((err) => console.error('Error cargando sorteos:', err));
  }, [fechaFiltro]);

  useEffect(() => {
    if (!selectedSorteoId) return;

    setLoadingRows(true);
    const tipo = selectedSorteo?.tipo_id || '';
    const listaEndpoint = ['NORMAL', 'MEGA'].includes(tipo)
      ? `/listas/individuales/${selectedSorteoId}?fecha=${fechaFiltro}`
      : `/listas/consolidada/${selectedSorteoId}?fecha=${fechaFiltro}`;
    Promise.all([
      fetchAPI<any[]>(listaEndpoint),
      fetchAPI<ResumenLocalRow[]>(`/listas/resumen-locales/${selectedSorteoId}?fecha=${fechaFiltro}`),
    ])
      .then(([listaData, resumenData]) => {
        if (tipo === 'NORMAL') {
          const agr: Record<string, number> = {};
          for (const r of Array.isArray(listaData) ? listaData : []) {
            const n = String(r.numero).padStart(2, '0');
            agr[n] = (agr[n] || 0) + (Number(r.monto) || 0);
          }
          setRows(Object.entries(agr).map(([numero, vendido]) => ({ numero, vendido })));
        } else if (tipo === 'FECHAS') {
          // Backend devuelve monto_total, frontend espera vendido
          setRows((listaData || []).map((r: any) => ({
            numero: r.numero,
            vendido: Number(r.monto_total) || 0,
          })));
        } else {
          setRows(listaData || []);
        }
        setResumen((resumenData || [])[0] || null);
      })
      .catch((err) => {
        console.error('Error cargando listas local:', err);
        setRows([]);
        setResumen(null);
      })
      .finally(() => setLoadingRows(false));
  }, [selectedSorteoId, selectedSorteo?.tipo_id, fechaFiltro]);

  // Cargar estado de workflow cuando cambia el sorteo seleccionado
  const cargarWorkflow = (sorteoId: string) => {
    Promise.all([
      fetchAPI<ListaVendedorRecibida[]>(`/listas/local/recibidas?sorteo_id=${sorteoId}`),
      fetchAPI<MiListaLocal | null>(`/listas/local/mi-lista?sorteo_id=${sorteoId}`),
    ]).then(([recv, milista]) => {
      setRecibidas(recv || []);
      setMiLista(milista);
      setSeleccionadas([]);
    }).catch(() => {
      setRecibidas([]);
      setMiLista(null);
    });
  };

  useEffect(() => {
    if (!selectedSorteoId) { setRecibidas([]); setMiLista(null); return; }
    cargarWorkflow(selectedSorteoId);
  }, [selectedSorteoId]);

  const handleConsolidar = async () => {
    if (!selectedSorteoId || seleccionadas.length === 0 || loadingWorkflow) return;
    setLoadingWorkflow(true);
    setMsgWorkflow('');
    try {
      await fetchAPI('/listas/local/consolidar', {
        method: 'POST',
        body: JSON.stringify({ sorteo_id: selectedSorteoId, lista_vendedor_ids: seleccionadas }),
      });
      setMsgWorkflow('Listas consolidadas correctamente.');
      cargarWorkflow(selectedSorteoId);
    } catch (err: any) {
      setMsgWorkflow(err?.message || 'Error al consolidar');
    } finally {
      setLoadingWorkflow(false);
    }
  };

  const handleEnviarBanca = async () => {
    if (!miLista || loadingWorkflow) return;
    setLoadingWorkflow(true);
    setMsgWorkflow('');
    try {
      await fetchAPI('/listas/local/enviar-banca', {
        method: 'POST',
        body: JSON.stringify({ lista_local_id: miLista.id }),
      });
      setMsgWorkflow('Lista enviada a banca exitosamente.');
      cargarWorkflow(selectedSorteoId);
    } catch (err: any) {
      setMsgWorkflow(err?.message || 'Error al enviar a banca');
    } finally {
      setLoadingWorkflow(false);
    }
  };

  const handlePrint = async (anchoMM: 80 | 58 = 80) => {
    if (printing) return; // Guard doble-click

    const tipo = selectedSorteo?.tipo_id || 'NORMAL';
    const cols = columnsByTipo(tipo);
    const printRows = tipo === 'REVENTADO' ? rows : mapMontos(rows, montoKeysByTipo[tipo] || ['vendido']);
    const isSunmi = isNativePrintSelected();

    console.log('[APK LIST] ─────────────────────────────────');
    console.log('[APK LIST] step 1 click');
    console.log('[APK LIST] componente: LocalListasPage');
    console.log('[APK LIST] plataforma:', (window as any).Capacitor?.getPlatform?.() || 'web');
    console.log('[APK LIST] isNativePrintSelected:', isSunmi);
    console.log('[APK LIST] sorteo:', selectedSorteo?.nombre, 'tipo:', tipo);
    console.log('[APK LIST] filas:', printRows.length);

    setPrinting(true);
    setPrintError(null);
    setPrintSuccess(false);

    // Safety timeout: GARANTIZA que printing se limpia en max 10 segundos
    const safetyTimer = setTimeout(() => {
      console.error('[APK LIST] SAFETY TIMEOUT 10s - forzando cleanup');
      setPrinting(false);
      setPrintError('Timeout: la impresión tardó demasiado. Intenta de nuevo.');
    }, 10000);

    try {
      // APK nativa: texto térmico directo via router
      if (isSunmi) {
        console.log('[APK LIST] step 2 entrando ruta nativa (printListByDestination)');

        const result = await printListByDestination({
          localNombre: resumen?.local_nombre || 'LOCAL',
          sorteoNombre: selectedSorteo?.nombre || '',
          tipo,
          fecha: fechaFiltro,
          totalVendedores: resumen?.total_vendedores || 0,
          totalJugadas: resumen?.total_jugadas || 0,
          totalMonto: resumen?.total_monto || 0,
          filas: printRows,
          columnas: cols.map(c => ({ key: c.key, label: c.label })),
        });

        if (result.ok) {
          console.log('[APK LIST] step 5 success');
          setPrintSuccess(true);
          setTimeout(() => setPrintSuccess(false), 3000);
        } else {
          console.error('[APK LIST] step 5 falló:', result.error);
          setPrintError(result.error || 'Error al imprimir');
        }
        return;
      }

      // Web: HTML popup
      console.log('[APK LIST] ruta web seleccionada (no es SUNMI)');
      const thead = `<tr>${cols.map((c) => `<th>${c.label}</th>`).join('')}</tr>`;
      const tbody = printRows
        .map((row) => `<tr>${cols.map((c) => `<td>${row[c.key] ?? ''}</td>`).join('')}</tr>`)
        .join('');

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
        <title>Lista ${selectedSorteo?.nombre || ''}</title>
        <style>
          @page { size: ${anchoMM}mm auto; margin: 0; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            width: ${anchoMM}mm;
            max-width: ${anchoMM}mm;
            overflow-x: hidden;
            font-family: 'Lucida Console', monospace;
            font-size: ${anchoMM === 58 ? 13 : 15}px;
            font-weight: 700;
            line-height: 1.35;
            color: #000;
            background: #fff;
            padding: 4px 5px 8px;
          }
          .center { text-align: center; }
          .title { font-size: ${anchoMM === 58 ? 16 : 19}px; font-weight: 900; margin-top: 2px; }
          .subtitle { font-size: ${anchoMM === 58 ? 13 : 15}px; font-weight: 700; }
          .info { font-size: ${anchoMM === 58 ? 12 : 14}px; font-weight: 700; margin: 2px 0; }
          .sep { border: 0; border-top: 1px dashed #000; margin: 4px 0; }
          table { width: 100%; border-collapse: collapse; }
          th { font-size: ${anchoMM === 58 ? 12 : 14}px; font-weight: 900; text-align: left; padding: 2px 4px; border-bottom: 1px solid #000; }
          td { font-size: ${anchoMM === 58 ? 12 : 14}px; font-weight: 700; padding: 1px 4px; border-bottom: 1px dotted #ccc; }
          tr:nth-child(even) td { background: #f5f5f5; }
          .resumen { margin-top: 4px; }
          .resumen-row { display: grid; grid-template-columns: 1fr auto; font-weight: 900; font-size: ${anchoMM === 58 ? 14 : 16}px; margin: 2px 0; }
          .resumen-row span:last-child { text-align: right; }
          .footer { margin-top: 4px; font-size: ${anchoMM === 58 ? 11 : 13}px; font-weight: 700; text-align: center; }
          @media print { body { margin: 0; padding: 2px 3px; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style></head><body>
        <div class="center title">${resumen?.local_nombre || 'LOCAL'}</div>
        <div class="center subtitle">${selectedSorteo?.nombre || ''} (${tipo})</div>
        <hr class="sep"/>
        <div class="info">FECHA: ${fechaFiltro}</div>
        <div class="info">JUGADAS: ${printRows.length}</div>
        <div class="info">VENDEDORES: ${resumen?.total_vendedores || 0}</div>
        <hr class="sep"/>
        <table><thead>${thead}</thead><tbody>${tbody}</tbody></table>
        <hr class="sep"/>
        <div class="resumen">
          <div class="resumen-row"><span>MONTO TOTAL:</span><span>${monto(resumen?.total_monto || 0)}</span></div>
          <div class="resumen-row"><span>JUGADAS:</span><span>${resumen?.total_jugadas || 0}</span></div>
        </div>
        <hr class="sep"/>
        <div class="footer">TIEMPOS BARAHONA</div>
      </body></html>`;

      const w = anchoMM === 58 ? 420 : 560;
      const printWindow = window.open('', '_blank', `width=${w},height=700`);
      if (!printWindow) throw new Error('No se pudo abrir ventana de impresión');

      let cerrada = false;
      const cerrar = () => {
        if (!cerrada) { cerrada = true; try { printWindow.close(); } catch (_) { /* */ } }
      };
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.onload = () => {
        if ('onafterprint' in printWindow) { printWindow.onafterprint = cerrar; }
        setTimeout(() => { printWindow.focus(); printWindow.print(); setTimeout(cerrar, 2000); }, 400);
      };
      console.log('[APK LIST] ✓ web popup lanzado');
    } catch (err: any) {
      const errMsg = err?.message || String(err) || 'Error desconocido';
      console.error('[APK LIST] step 5 error:', errMsg);
      setPrintError(errMsg);
    } finally {
      clearTimeout(safetyTimer);
      setPrinting(false);
      console.log('[APK LIST] step 6 finally - printing cleared');
      console.log('[APK LIST] ─────────────────────────────────');
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Listas Consolidadas del Local</h1>
        <p className="text-gray-500">Solo incluye ventas de sus vendedores y su local</p>
      </div>

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-sm text-gray-600">Fecha</label>
            <input
              type="date"
              value={fechaFiltro}
              onChange={(e) => setFechaFiltro(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="text-sm text-gray-600">Sorteo</label>
            <select
              value={selectedSorteoId}
              onChange={(e) => setSelectedSorteoId(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg"
            >
              {sorteos.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre} ({s.tipo_id}){(s.jugadas_hoy ?? 0) > 0 ? ` — ${s.jugadas_hoy} jug.` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="text-sm text-gray-600 flex items-end">
            Tipo seleccionado: <span className="ml-1 font-semibold">{selectedSorteo?.tipo_id || '-'}</span>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card><p className="text-sm text-gray-500">Vendedores activos</p><p className="text-xl font-bold">{resumen?.total_vendedores || 0}</p></Card>
        <Card><p className="text-sm text-gray-500">Jugadas activas</p><p className="text-xl font-bold">{resumen?.total_jugadas || 0}</p></Card>
        <Card><p className="text-sm text-gray-500">Monto vendido</p><p className="text-xl font-bold">₡{monto(resumen?.total_monto || 0)}</p></Card>
      </div>

      <Card>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-900">Lista consolidada del local</h3>
          <div className="flex items-center gap-2">
            {/* Feedback de estado */}
            {printSuccess && (
              <span className="text-xs text-green-600 font-medium">✓ Impreso</span>
            )}
            {printError && (
              <span className="text-xs text-red-600 font-medium max-w-[200px] truncate" title={printError}>
                ✗ {printError}
              </span>
            )}
            <button
              onClick={() => handlePrint(80)}
              disabled={rows.length === 0 || printing}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border transition ${
                printing
                  ? 'bg-blue-100 text-blue-700 border-blue-300 cursor-wait'
                  : printSuccess
                  ? 'bg-green-100 text-green-700 border-green-300'
                  : 'bg-gray-100 hover:bg-gray-200 border-gray-300'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              <Printer size={15} />
              {printing ? 'Imprimiendo...' : (printSuccess ? '✓ Listo' : 'Imprimir')}
            </button>
          </div>
        </div>
        {loadingRows ? (
          <p className="text-sm text-gray-500">Cargando lista...</p>
        ) : (selectedSorteo?.tipo_id === 'NORMAL' || selectedSorteo?.tipo_id === 'REVENTADO') ? (
          <div className="overflow-x-auto">
            <NumerosGrid
              rows={rows}
              tipo={selectedSorteo?.tipo_id as TipoNumerosGrid}
              hidePrintButton
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <DynamicListTable
              columns={columnsByTipo(selectedSorteo?.tipo_id || 'NORMAL')}
              rows={mapMontos(rows, montoKeysByTipo[selectedSorteo?.tipo_id || ''] || ['vendido'])}
              onSave={async () => { }}
              idField="id"
            />
          </div>
        )}
      </Card>

      {/* Workflow: consolidar vendedores y enviar a banca */}
      {selectedSorteoId && (
        <Card>
          <h3 className="font-semibold text-gray-900 mb-3">Workflow — Envío a Banca</h3>

          {msgWorkflow && (
            <div className={`mb-3 px-3 py-2 rounded text-sm font-medium ${
              msgWorkflow.toLowerCase().includes('error') || msgWorkflow.toLowerCase().includes('error')
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-green-50 text-green-800 border border-green-200'
            }`}>
              {msgWorkflow}
            </div>
          )}

          {/* Listas de vendedores recibidas */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">
              Listas recibidas de vendedores ({recibidas.length} pendientes)
            </h4>
            {recibidas.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No hay listas de vendedores pendientes de consolidar.</p>
            ) : (
              <div className="space-y-1">
                {recibidas.map((lv) => (
                  <label key={lv.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={seleccionadas.includes(lv.id)}
                      onChange={(e) => {
                        setSeleccionadas(prev =>
                          e.target.checked ? [...prev, lv.id] : prev.filter(x => x !== lv.id)
                        );
                      }}
                      className="w-4 h-4"
                    />
                    <span className="font-medium">{lv.vendedor_nombre}</span>
                    <span className="text-gray-500">— {lv.jugadas_count} jugadas</span>
                    <span className="text-gray-400 text-xs">{lv.enviada_at ? lv.enviada_at.replace('T', ' ').slice(0, 16) : ''}</span>
                  </label>
                ))}
                <button
                  onClick={handleConsolidar}
                  disabled={seleccionadas.length === 0 || loadingWorkflow}
                  className="mt-2 px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-blue-700 transition"
                >
                  {loadingWorkflow ? 'Procesando...' : `Consolidar ${seleccionadas.length} seleccionadas`}
                </button>
              </div>
            )}
          </div>

          {/* Estado de mi lista local */}
          <div className="border-t pt-3">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Mi lista local</h4>
            {!miLista ? (
              <p className="text-sm text-gray-500 italic">No hay lista local activa para este sorteo.</p>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  miLista.estado === 'ENVIADA_BANCA'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {miLista.estado}
                </span>
                <span className="text-sm text-gray-600">
                  {miLista.listas_vendedor_count} listas de vendedores · {miLista.jugadas_count} jugadas
                </span>
                {miLista.enviada_banca_at && (
                  <span className="text-xs text-gray-400">
                    Enviada: {miLista.enviada_banca_at.replace('T', ' ').slice(0, 16)}
                  </span>
                )}
                {miLista.estado === 'ACTIVA_CONSOLIDACION' && (
                  <button
                    onClick={handleEnviarBanca}
                    disabled={loadingWorkflow}
                    className="px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-indigo-700 transition"
                  >
                    {loadingWorkflow ? 'Enviando...' : 'Enviar a Banca'}
                  </button>
                )}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default LocalListasPage;
